import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Button, 
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import subscriptionService from '../services/subscriptionService';
import stripeService from '../services/stripeService';

const PricingCard = ({ plan, isCurrentPlan = false }) => {
  const navigate = useNavigate();
  const isAuthenticated = authService.isAuthenticated();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [annualPricing, setAnnualPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState('');
  
  // Only show billing toggle for paid plans (not free)
  const showBillingToggle = plan.slug !== 'free';
  
  // Fetch annual pricing from the service
  useEffect(() => {
    if (showBillingToggle) {
      const fetchAnnualPricing = async () => {
        try {
          setLoading(true);
          const pricingData = await subscriptionService.getAnnualPricing(plan.slug);
          setAnnualPricing(pricingData);
        } catch (err) {
          console.error(`Failed to load annual pricing for ${plan.slug}:`, err);
        } finally {
          setLoading(false);
        }
      };
      
      fetchAnnualPricing();
    }
  }, [plan.slug, showBillingToggle]);
  
  // Get current price and period based on selected billing cycle
  const getPriceDetails = () => {
    if (billingCycle === 'annual' && showBillingToggle && annualPricing) {
      return {
        price: annualPricing.price,
        period: annualPricing.period,
        savings: annualPricing.savings
      };
    } else {
      return {
        price: plan.price,
        period: plan.period
      };
    }
  };
  
  const handlePricingButtonClick = async () => {
    setError('');
    
    if (!isAuthenticated) {
      // If not authenticated, navigate to registration
      navigate('/register', {
        state: { 
          returnTo: '/subscription',
          plan: plan.slug,
          billingCycle: billingCycle
        }
      });
      return;
    }

    // Handle free plan
    if (plan.slug === 'free') {
      navigate('/subscription');
      return;
    }

    // Handle Stripe checkout for paid plans
    try {
      setProcessingPayment(true);
      
      // Simple redirect to Stripe checkout
      await stripeService.redirectToCheckout(plan.slug, billingCycle);
      
    } catch (error) {
      console.error('Payment error:', error);
      setError(
        error.response?.data?.error || 
        'Failed to initiate payment. Please try again.'
      );
    } finally {
      setProcessingPayment(false);
    }
  };
  
  const handleBillingChange = (event, newBillingCycle) => {
    if (newBillingCycle !== null) {
      setBillingCycle(newBillingCycle);
    }
  };
  
  const priceDetails = getPriceDetails();

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <Box 
        sx={{ 
          p: 3, 
          backgroundColor: plan.color === 'primary' 
            ? 'primary.main' 
            : plan.color === 'secondary' 
              ? 'grey.900'
              : 'grey.500',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" component="h3" gutterBottom>
          {plan.name}
        </Typography>
        
        {showBillingToggle && (
          <>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              onChange={handleBillingChange}
              aria-label="billing cycle"
              size="small"
              sx={{ 
                mb: 2, 
                '& .MuiToggleButton-root': {
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  }
                }
              }}
            >
              <ToggleButton value="monthly" aria-label="monthly billing">
                Monthly
              </ToggleButton>
              <ToggleButton value="annual" aria-label="annual billing">
                Annual
              </ToggleButton>
            </ToggleButtonGroup>
            
            {loading && <CircularProgress size={20} sx={{ color: 'white', opacity: 0.7 }} />}
          </>
        )}
        
        <Typography variant="h3" component="div">
          {priceDetails.price}
        </Typography>
        <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>
          {priceDetails.period}
        </Typography>
        {billingCycle === 'annual' && priceDetails.savings && (
          <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.8)' }}>
            {priceDetails.savings}
          </Typography>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: '0.875rem' }}>
            {error}
          </Alert>
        )}
        
        <List dense>
          {plan.features.map((feature, featureIndex) => (
            <ListItem key={featureIndex}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {feature.included ? (
                  <CheckIcon color="success" />
                ) : (
                  <CloseIcon color="disabled" />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={feature.name} 
                sx={{ 
                  textDecoration: feature.included ? 'none' : 'line-through',
                  color: feature.included ? 'text.primary' : 'text.disabled'
                }}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
      <Box sx={{ p: 2, pt: 0 }}>
        <Button 
          fullWidth 
          variant={plan.buttonVariant}
          color={plan.color === 'grey' ? 'primary' : plan.color}
          onClick={handlePricingButtonClick}
          disabled={isCurrentPlan || processingPayment}
          startIcon={processingPayment ? <CircularProgress size={20} /> : null}
        >
          {processingPayment ? 'Redirecting...' : 
           isCurrentPlan ? 'Current Plan' : 
           !isAuthenticated ? 'Get Started' :
           plan.buttonText}
        </Button>
      </Box>
    </Card>
  );
};

export default PricingCard;